import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useApproveAttachments } from 'hooks/api/useAttachments';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type ApproveAttachmentsParamsT = {
  ticketId: string;
  fileNames: string[];
};

type UseApproveAttachmentsPopupT = {
  isApprovingAttachments: boolean;
  handleApproveAttachments: (params: ApproveAttachmentsParamsT, onClose: NoopT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useApproveAttachmentsPopup = (): UseApproveAttachmentsPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const approveAttachmentsMutation = useApproveAttachments({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
    },
  });

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );
  const handleApproveAttachments = useCallback(
    (params: ApproveAttachmentsParamsT, onClose: NoopT) => () => {
      approveAttachmentsMutation.mutate(params, {
        onSuccess: () => {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: 'Attachments successfully approved',
              snackBarSeverity: 'success',
            })
          );
          onClose();
        },
        onError: error => {
          const errorMsg =
            getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
              snackBarSeverity: 'error',
            })
          );
          onClose();
        },
      });
    },
    [approveAttachmentsMutation, dispatch]
  );

  return {
    isApprovingAttachments: approveAttachmentsMutation.isLoading,
    handleApproveAttachments,
    handleClosePopup,
  };
};

export default useApproveAttachmentsPopup;
